import { Head, Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { ImpersonationBanner } from "@/components/ImpersonationBanner"
import { ProgressBar } from "./components/ProgressBar"
import { Welcome } from "./steps/Welcome"
import { AuditIntro } from "./steps/AuditIntro"
import { Audit } from "./steps/Audit"
import { WheelReview } from "./steps/WheelReview"
import { PeerQuestions } from "./steps/PeerQuestions"
import { PersonalGoals } from "./steps/PersonalGoals"
import { OneYearPlan } from "./steps/OneYearPlan"
import { Kpis } from "./steps/Kpis"
import { Kois } from "./steps/Kois"
import { Imperatives } from "./steps/Imperatives"
import { Ministry } from "./steps/Ministry"
import { QuarterRocks } from "./steps/QuarterRocks"
import { Review } from "./steps/Review"

const STEP_COMPONENTS = {
  welcome: Welcome,
  audit_intro: AuditIntro,
  audit: Audit,
  wheel_review: WheelReview,
  peer_questions: PeerQuestions,
  personal_goals: PersonalGoals,
  one_year_plan: OneYearPlan,
  kpis: Kpis,
  kois: Kois,
  imperatives: Imperatives,
  ministry: Ministry,
  quarter_rocks: QuarterRocks,
  review: Review,
}

// Logged-in members / admins get a "back to dashboard" link; guests in
// mid-intake get "End session" so they can close out on a shared device.
function portalBackTarget(currentUser) {
  if (!currentUser) return null
  if (currentUser.isAdmin) return { href: "/app/admin", label: "Back to admin" }
  if (currentUser.isCoach) return { href: "/app/coach", label: "Back to coach portal" }
  if (currentUser.role === "member") return { href: "/app/member", label: "Back to dashboard" }
  return null
}

export default function IntakeIndex(props) {
  const { wizard, currentUser } = props
  const step = wizard.current
  const StepComponent = STEP_COMPONENTS[step] || Welcome
  const backTarget = portalBackTarget(currentUser)
  const isGuest = currentUser?.role === "guest" && !currentUser?.isAdmin

  return (
    <div className="min-h-screen bg-zinc-50">
      <Head title="Your Assessment" />
      <ImpersonationBanner />

      <header className="bg-white border-b border-zinc-200 px-4 sm:px-6 py-4 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            {backTarget && (
              <Link
                href={backTarget.href}
                className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-kf-red"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">{backTarget.label}</span>
              </Link>
            )}
            <div className="min-w-0">
              <p className="font-display text-lg text-zinc-900 truncate">
                Kingdom Factor Assessment
              </p>
              <p className="text-xs text-zinc-500 truncate">
                {currentUser?.name || currentUser?.email}
              </p>
            </div>
          </div>
          {isGuest && (
            <Link
              href="/logout"
              method="post"
              as="button"
              className="text-xs text-zinc-500 hover:text-zinc-900 shrink-0"
            >
              End session
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <ProgressBar wizard={wizard} />
        <StepComponent {...props} />
      </main>
    </div>
  )
}
